import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { 
  Dialog,
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter 
} from "@/components/ui/dialog";
import { 
  Form, 
  FormControl, 
  FormField, 
  FormItem, 
  FormLabel, 
  FormMessage,
  FormDescription
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Loader2, X, FileText, User, Plus } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { candidateFoldersAPI, candidatesAPI2, authAPI } from "@/services/api";
import { Textarea } from "@/components/ui/textarea";
import { uploadFileToS3, validateFile } from "@/utils/s3Upload";
import CreateFolderModal from "./CreateFolderModal";

export default function AddCandidateModal({ isOpen, onClose, onSuccess }) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [folders, setFolders] = useState([]);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [resumeFile, setResumeFile] = useState(null);
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [isCreateFolderOpen, setIsCreateFolderOpen] = useState(false);
  const resumeInputRef = useRef(null);
  const profileImageInputRef = useRef(null);

  const form = useForm({
    defaultValues: {
      name: "",
      email: "",
      mobileNumber: "",
      address: "",
      folderId: "",
      sendWelcomeEmail: true
    }
  });

  useEffect(() => {
    if (isOpen) {
      form.reset({
        name: "",
        email: "",
        mobileNumber: "",
        address: "",
        folderId: "",
        sendWelcomeEmail: true
      });
      setResumeFile(null);
      setProfileImageFile(null);
      setProfileImagePreview(null);
      fetchFolders();
      fetchUserProfile();
    }
  }, [isOpen, form]);

  const fetchUserProfile = async () => {
    try {
      const response = await authAPI.getProfile();
      setUserProfile(response.data);
    } catch (error) {
      console.error("Error fetching user profile:", error);
    }
  };

  const fetchFolders = async () => {
    try {
      setLoadingFolders(true);
      const response = await candidateFoldersAPI.getAll();
      setFolders(response?.data?.data || []);
    } catch (error) {
      console.error("Error fetching folders:", error);
      toast({
        title: "Error",
        description: "Failed to load folders",
        variant: "destructive",
      });
    } finally {
      setLoadingFolders(false);
    }
  };

  const handleCreateFolderSuccess = () => {
    fetchFolders();
  };

  const handleResumeChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        validateFile(file, 'resume');
        setResumeFile(file);
      } catch (error) {
        toast({
          title: "Invalid File",
          description: error.message,
          variant: "destructive",
        });
        e.target.value = null;
      }
    }
  };

  const handleProfileImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        validateFile(file, 'profileImage');
        setProfileImageFile(file);
        
        // Create preview
        const reader = new FileReader();
        reader.onloadend = () => {
          setProfileImagePreview(reader.result);
        };
        reader.readAsDataURL(file);
      } catch (error) {
        toast({
          title: "Invalid File",
          description: error.message,
          variant: "destructive",
        });
        e.target.value = null;
      }
    }
  };

  const removeResume = () => {
    setResumeFile(null);
    if (resumeInputRef.current) {
      resumeInputRef.current.value = null;
    }
  };

  const removeProfileImage = () => {
    setProfileImageFile(null);
    setProfileImagePreview(null);
    if (profileImageInputRef.current) {
      profileImageInputRef.current.value = null;
    }
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      
      // Validate resume is provided
      if (!resumeFile) {
        toast({
          title: "Resume Required",
          description: "Please upload a resume (PDF format only)",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }


      // Get user profile for companyId
      if (!userProfile) {
        throw new Error('User profile not loaded');
      }

      const companyId = userProfile.companyId;

      // Upload files to S3 first
      setUploadingFiles(true);

      const fileUploads = [];
      
      if (resumeFile) {
        fileUploads.push({ file: resumeFile, fileType: 'resume' });
      }
      
      if (profileImageFile) {
        fileUploads.push({ file: profileImageFile, fileType: 'profileImage' });
      }

      // Create temporary candidate ID for S3 folder structure
      const tempCandidateId = `temp_${Date.now()}`;
      
      let fileUrls = {};
      
      for (const { file, fileType } of fileUploads) {
        try {
          const url = await uploadFileToS3(file, companyId, tempCandidateId, fileType);
          fileUrls[fileType] = url;
        } catch (uploadError) {
          console.error(`❌ Frontend: Failed to upload ${fileType}:`, uploadError);
          throw new Error(`Failed to upload ${fileType}: ${uploadError.message}`);
        }
      }

      setUploadingFiles(false);

      // Prepare candidate data with S3 URLs
      const candidateData = {
        name: data.name,
        email: data.email,
        mobileNumber: data.mobileNumber || '',
        address: data.address || '',
        folderId: data.folderId || '',
        sendWelcomeEmail: data.sendWelcomeEmail,
        resume: fileUrls.resume || null,
        profileImage: fileUrls.profileImage || null
      };


      // Send to backend with URLs
      const response = await candidatesAPI2.create(candidateData);
      
      
      toast({
        title: "Success",
        description: "Candidate created successfully",
      });
      
      onSuccess(response.data);
      onClose();
    } catch (error) {
      console.error("❌ Frontend: Error creating candidate:", error);
      const errorMessage = error.response?.data?.message || 
                         (error.response?.data?.errors?.length 
                          ? error.response.data.errors[0].msg 
                          : error.message || "Failed to create candidate");
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setUploadingFiles(false);
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[80vw] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Candidate</DialogTitle>
          </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 pt-4">
            <FormField
              control={form.control}
              name="name"
              rules={{ required: "Name is required" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Full Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter full name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="email"
              rules={{ 
                required: "Email is required",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Invalid email address"
                }
              }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input 
                      type="email" 
                      placeholder="Enter email" 
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="mobileNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Mobile Number</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter mobile number" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="address"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Address (Optional)</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Enter address" 
                      className="resize-none"
                      rows={3}
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">

            <FormField
              control={form.control}
              name="folderId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Folder (Optional)</FormLabel>
                  <div className="flex items-center gap-2">
                    <Select onValueChange={field.onChange} value={field.value || ""}>
                      <div className="pt-[16px] flex-1">
                        <FormControl>
                          <SelectTrigger className="w-full h-10 px-3 text-sm">
                            <SelectValue placeholder="Select a folder or leave empty for Others" />
                          </SelectTrigger>
                        </FormControl>
                      </div>
                      <SelectContent>
                        {loadingFolders ? (
                          <SelectItem value="loading" disabled>
                            Loading folders...
                          </SelectItem>
                        ) : (
                          folders.map((folder) => (
                            <SelectItem key={folder._id} value={folder._id}>
                              <div className="flex items-center">
                                <div 
                                  className="mr-2 h-3 w-3 rounded-full"
                                  style={{ backgroundColor: folder.color }}
                                />
                                {folder.name}
                              </div>
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-10 w-10 mt-4"
                            onClick={() => setIsCreateFolderOpen(true)}
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Add Folder</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Resume Upload - Mandatory */}
            <div className="space-y-2">
              <FormLabel className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Resume <span className="text-red-500">*</span>
              </FormLabel>
              <FormDescription className="text-xs">
                Only PDF format is allowed
              </FormDescription>
              <div className="flex items-center gap-2">
                <Input
                  ref={resumeInputRef}
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={handleResumeChange}
                  className="flex-1"
                />
                {resumeFile && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={removeResume}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
              {resumeFile && (
                <p className="text-sm text-green-600 flex items-center gap-1">
                  <FileText className="h-4 w-4" />
                  {resumeFile.name}
                </p>
              )}
            </div>

            {/* Profile Picture Upload - Optional */}
            <div className="space-y-2">
              <FormLabel className="flex items-center gap-2">
                <User className="h-4 w-4" />
                Profile Picture (Optional)
              </FormLabel>
              <FormDescription className="text-xs">
                Upload a profile picture (JPG, PNG, etc.)
              </FormDescription>
              <div className="flex items-center gap-2">
                <Input
                  ref={profileImageInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleProfileImageChange}
                  className="flex-1"
                />
                {profileImageFile && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={removeProfileImage}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
              {profileImagePreview && (
                <div className="mt-2">
                  <img
                    src={profileImagePreview}
                    alt="Profile preview"
                    className="h-20 w-20 rounded-full object-cover border-2 border-gray-200"
                  />
                </div>
              )}
            </div>

            </div>

            <DialogFooter className="pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={loading || uploadingFiles}>
                {(loading || uploadingFiles) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {uploadingFiles ? 'Uploading Files...' : loading ? 'Creating...' : 'Add Candidate'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>

    <CreateFolderModal
      isOpen={isCreateFolderOpen}
      onClose={() => setIsCreateFolderOpen(false)}
      onSuccess={handleCreateFolderSuccess}
    />
    </>
  );
}